/**
 * The §10-7 record as a file (plan 0026 P2, step 4; AML §10-7: a running record of worked time
 * the employer answers for, available to the employee and the tillitsvalgte). One line per
 * registration in the period, every person on the screen, whether approved or not: the record is
 * what happened, pay is what was approved. Semicolons and a BOM, so Excel in Norway opens it.
 *
 * Arbeidstilsynet lists what each line holds: "navn, dato, daglig arbeidstid: start- og
 * sluttidspunkt", "eventuell overtid: start- og sluttidspunkt", where "kun overtid etter
 * arbeidsmiljøloven § 10-6 skal registreres som overtid", and for breaks "pauser som er arbeidstid"
 * and "pauser som ikke er arbeidstid", each with "varighet (minutter) og plassering". The overtime
 * and the break columns were added on 2026-09-23 (law register REC-10-7-5, REC-10-7-6).
 */
import type { ApiShift, ApiUser } from '@/api/data';
import { oslo, workedResult } from '@/rules';
import type { BreakPolicy } from '@timefly/core';
import { isHolyDay, mondayOf } from '@timefly/core';

const HEAD = [
  'Ansattnr',
  'Navn',
  'Dato',
  'Planlagt',
  'Inn',
  'Ut',
  'Pause (min)',
  'Pause plassering',
  'Pause er arbeidstid',
  'Overtid start',
  'Overtid slutt',
  'Timer etter pause',
  'Natt',
  'Søn-/helgedag',
  'Godkjent',
];
const cell = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
const comma = (n: number) => n.toFixed(2).replace('.', ',');

/**
 * The ordinary day and week past which a person's hours are § 10-6 overtime, from their
 * membership: § 10-4(1) "ni timer i løpet av 24 timer og 40 timer i løpet av sju dager"; under a
 * § 10-5 averaging agreement "ti timer i løpet av 24 timer og 48 timer i løpet av sju dager"
 * (first paragraph) or 12,5 and 48 with the tillitsvalgte (second paragraph). The same table as
 * backend services/payrollMath.js `limitsOf`, which pay's daily line and the validators' § 10-6
 * caps read. Null for a § 10-12 position: "Bestemmelsene i dette kapittel kommer ikke til
 * anvendelse", so there is no § 10-6 overtime to record (pay has no overtime line for them
 * either). Lovdata, arbeidsmiljøloven kapittel 10, read 2026-10-19. Until 2026-10-19 the record
 * used 9 and 40 for everyone (#112). A reader without people.manage gets no `averaging` and so
 * the § 10-4 lines, as the planner's rule engine does (web/src/rules toPerson).
 */
export function ordinaryLimits(
  u: Pick<ApiUser, 'averaging' | 'exempt_position'> | undefined,
): { day: number; week: number } | null {
  if (u?.exempt_position) return null;
  if (u?.averaging === 'tillitsvalgte') return { day: 12.5, week: 48 };
  if (u?.averaging === 'individual') return { day: 10, week: 48 };
  return { day: 9, week: 40 };
}

/**
 * The statutory overtime of one registration, in hours: what passes the ordinary day, plus what
 * passes the ordinary week once the ordinary hours before it are counted. `weekSoFar` carries the
 * running ordinary hours of that person's week and is updated.
 */
function overtimeHours(
  netHours: number,
  weekSoFar: { hours: number },
  limits: { day: number; week: number },
): number {
  const daily = Math.max(0, netHours - limits.day);
  const ordinary = netHours - daily;
  const weekly = Math.max(0, Math.min(ordinary, weekSoFar.hours + ordinary - limits.week));
  weekSoFar.hours += ordinary;
  return daily + weekly;
}

/**
 * AML §10-9(2): minutes of a punched break the law makes working time. "Varer arbeidstiden mer
 * enn to timer etter at den alminnelige arbeidstid er avviklet, skal arbeidstaker gis en pause på
 * minst en halv time. Pausen regnes som en del av arbeidstiden." So a break that starts after the
 * planned end, with more than two hours worked past that end (the break not counted), is working
 * time up to 30 minutes. The same test as services/payrollMath.js paidOvertimeBreakHours.
 */
function paidOvertimeBreakMinutes(s: ApiShift): number {
  const ms = (x: string | null | undefined) => (x ? new Date(x).getTime() : Number.NaN);
  const bIn = ms(s.lunch_in_time);
  const bOut = ms(s.lunch_out_time);
  const planEnd = ms(s.end_time);
  const out = ms(s.punch_out_time);
  if (![bIn, bOut, planEnd, out].every(Number.isFinite) || !s.punch_in_time) return 0;
  if (bIn < planEnd || bOut <= bIn) return 0;
  const afterEnd = (out - planEnd - (bOut - bIn)) / 36e5;
  return afterEnd > 2 ? Math.min(30, (bOut - bIn) / 6e4) : 0;
}

/**
 * The worked minutes the record and its overtime count, as pay counts them: core's result, with the
 * §10-9(2) half hour given back where core deducted it (services/payrollMath.js deductedBreak does
 * the same), so the file does not show fewer hours than pay on the rows it marks as working time.
 * `paid` is the minutes given back.
 */
function recordMinutes(s: ApiShift, policy: BreakPolicy | null) {
  const r = workedResult(s, policy);
  if (r.status !== 'ok') return { r, minutes: 0, paid: 0, deducted: 0 };
  // core already keeps the §10-9(2) half hour as worked time (#252); here it is only named, never added again
  const paid = paidOvertimeBreakMinutes(s);
  return { r, minutes: r.minutes, paid, deducted: Math.round(r.deductedMinutes) };
}

/** `hours` before the end of the shift, as a clock time; the overtime of a day sits at its end. */
function backFrom(end: string | null | undefined, hours: number): string {
  const t = end ? new Date(end) : null;
  if (!t || Number.isNaN(t.getTime()) || hours <= 0) return '';
  return oslo(new Date(t.getTime() - hours * 36e5).toISOString())?.time ?? '';
}

/**
 * The § 10-6 overtime of each registration, as { from, to } clock labels, for the shifts of ONE
 * person in date order, counted from that person's ordinary lines (`ordinaryLimits`). The file and the print both read it, so the two records agree.
 */
export function overtimeWindows(
  shifts: ApiShift[],
  policy: BreakPolicy | null,
  person?: Pick<ApiUser, 'averaging' | 'exempt_position'>,
): Map<number, { from: string; to: string }> {
  const out = new Map<number, { from: string; to: string }>();
  const limits = ordinaryLimits(person);
  if (!limits) return out;
  const weeks = new Map<string, { hours: number }>();
  for (const s of [...shifts].sort((a, b) => String(a.start_time).localeCompare(String(b.start_time)))) {
    const d = oslo(s.start_time)?.date ?? '';
    if (!d || !s.punch_in_time) continue;
    const { r, minutes } = recordMinutes(s, policy);
    if (r.status !== 'ok') continue;
    // keyed by its Monday, so a week across a month or the new year is one week (2026-09-23);
    // the pages fetch from the Monday of the period's first week for the same reason
    const key = mondayOf(d);
    if (!weeks.has(key)) weeks.set(key, { hours: 0 });
    const ot = overtimeHours(minutes / 60, weeks.get(key) as { hours: number }, limits);
    if (ot > 0) {
      out.set(s.id, { from: backFrom(s.punch_out_time, ot), to: oslo(s.punch_out_time)?.time ?? '' });
    }
  }
  return out;
}

export function recordCsv(
  users: ApiUser[],
  shifts: ApiShift[],
  from: string,
  to: string,
  policy: BreakPolicy | null,
): string {
  const byId = new Map(users.map((u) => [u.id, u]));
  // one running week per person, on that person's own lines, so the week's line is drawn where the law draws it
  const windows = new Map<number, { from: string; to: string }>();
  for (const u of users) {
    const mine = shifts.filter((s) => s.user_id === u.id);
    for (const [id, w] of overtimeWindows(mine, policy, u)) windows.set(id, w);
  }
  const lines = shifts
    .filter((s) => s.user_id != null && byId.has(s.user_id) && s.punch_in_time)
    .map((s) => ({ s, d: oslo(s.start_time)?.date ?? '' }))
    .filter(({ d }) => d >= from && d <= to)
    .sort((a, b) => a.d.localeCompare(b.d) || (a.s.user_id ?? 0) - (b.s.user_id ?? 0))
    .map(({ s, d }) => {
      const u = byId.get(s.user_id as number) as ApiUser;
      const { r, minutes, paid, deducted } = recordMinutes(s, policy);
      const inT = oslo(s.punch_in_time)?.time ?? '';
      const outT = oslo(s.punch_out_time)?.time ?? '';
      const breakFrom = oslo(s.lunch_in_time)?.time ?? '';
      const breakTo = oslo(s.lunch_out_time)?.time ?? '';
      const clocked = breakFrom && breakTo;
      // a break that was taken but not deducted is working time: the company pays it, which is
      // where §10-9(1) lands a break nobody may leave the workplace for; and the half hour of a
      // break after a long overtime stretch is working time by §10-9(2), judged on this break, so a
      // longer one is working time in part (2026-09-23)
      const breakWork = !clocked ? '' : deducted === 0 ? 'ja' : paid > 0 ? `delvis (${paid} min)` : 'nei';
      const ot = windows.get(s.id);
      return [
        u.employee_id ?? '',
        [u.name, u.lastname].filter(Boolean).join(' '),
        d,
        `${oslo(s.start_time)?.time ?? ''}-${oslo(s.end_time)?.time ?? ''}`,
        inT,
        outT,
        r.status === 'ok'
          ? Math.round(clocked ? Math.max(deducted + paid, minutesBetween(breakFrom, breakTo)) : deducted)
          : '',
        clocked ? `${breakFrom}-${breakTo}` : deducted ? 'automatisk' : '',
        clocked ? breakWork : deducted ? 'nei' : '',
        ot ? ot.from : '',
        ot ? ot.to : '',
        r.status === 'ok' ? comma(minutes / 60) : '',
        inT && (inT < '06:00' || (outT && outT > '21:00') || (outT && outT < inT)) ? 'ja' : '',
        isHolyDay(d) ? 'ja' : '', // AML §10-10: a Sunday or a public holiday, not Sunday alone
        s.is_approved ? 'ja' : 'nei',
      ];
    });
  return `﻿${[HEAD, ...lines].map((r) => r.map(cell).join(';')).join('\n')}`;
}

/** Minutes between two Oslo clock labels on the same day, 0 when either is missing. */
function minutesBetween(from: string, to: string): number {
  if (!from || !to) return 0;
  const [fh, fm] = from.split(':').map(Number);
  const [th, tm] = to.split(':').map(Number);
  return Math.max(0, (th ?? 0) * 60 + (tm ?? 0) - ((fh ?? 0) * 60 + (fm ?? 0)));
}
